/**
 * ExposureLimitsSection Component
 * Total portfolio gauge + per-level cards with usage bars.
 * Alert/block thresholds visualized on bars. Inline editing.
 */

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Save, Info, ShieldAlert, Ban } from 'lucide-react';
import { ConfirmSaveModal } from '../ConfirmSaveModal';
import type { ExposureLimitItem } from '@/types/policy.types';

interface ExposureLimitsSectionProps {
  data: ExposureLimitItem[] | null;
  canEdit: boolean;
  onSave: (data: ExposureLimitItem[]) => void;
  isSaving: boolean;
}

const LEVEL_THEMES: Record<string, { color: string; label: string }> = {
  BRONZE: { color: '#CD7F32', label: 'Bronze' },
  SILVER: { color: '#94A3B8', label: 'Silver' },
  GOLD: { color: '#DAA520', label: 'Gold' },
  PLATINUM: { color: '#3B82F6', label: 'Platinum' },
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount);

function usagePct(current: number, max: number): number {
  if (max === 0) return 0;
  return (current / max) * 100;
}

function usageColor(pct: number, alert: number, block: number): string {
  if (pct >= block) return '#EF4444';
  if (pct >= alert) return '#F59E0B';
  return '#10B981';
}

// ─── Usage bar with colored zones ───────────────────────────────────────────

function UsageBar({ current, max, alert, block }: {
  current: number; max: number; alert: number; block: number;
}) {
  const pct = Math.min(usagePct(current, max), 100);
  const barColor = usageColor(pct, alert, block);

  return (
    <div className="relative h-4 w-full overflow-hidden rounded-full">
      {/* Zone: safe (0 → alert) */}
      <div
        className="absolute top-0 left-0 h-full"
        style={{ width: `${alert}%`, backgroundColor: 'rgba(16,185,129,0.15)' }}
      />
      {/* Zone: alert (alert → block) */}
      <div
        className="absolute top-0 h-full"
        style={{ left: `${alert}%`, width: `${block - alert}%`, backgroundColor: 'rgba(245,158,11,0.15)' }}
      />
      {/* Zone: block (block → 100) */}
      <div
        className="absolute top-0 h-full"
        style={{ left: `${block}%`, width: `${100 - block}%`, backgroundColor: 'rgba(239,68,68,0.12)' }}
      />
      {/* Filled bar */}
      <div
        className="absolute top-0 left-0 h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: barColor }}
      />
    </div>
  );
}

// ─── Currency input: shows formatted when idle, raw when focused ────────────

function CurrencyInput({ value, onChange, className }: {
  value: number; onChange: (v: number) => void; className?: string;
}) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  return (
    <input
      ref={ref}
      type={focused ? 'number' : 'text'}
      min="0"
      value={focused ? value : formatCurrency(value)}
      onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={className}
    />
  );
}

const formatNumber = (n: number) => n.toLocaleString('es-MX');

function NumberInput({ value, onChange, className }: {
  value: number; onChange: (v: number) => void; className?: string;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <input
      type={focused ? 'number' : 'text'}
      min="0"
      value={focused ? value : formatNumber(value)}
      onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={className}
    />
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ExposureLimitsSection({ data, canEdit, onSave, isSaving }: ExposureLimitsSectionProps) {
  const [draft, setDraft] = useState<ExposureLimitItem[] | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  if (!data || data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin limites de exposicion configurados.
      </p>
    );
  }

  function ensureDraft(): ExposureLimitItem[] {
    if (draft) return draft;
    const d = structuredClone(data!);
    setDraft(d);
    return d;
  }

  function updateItem(sk: string, field: keyof ExposureLimitItem, value: number) {
    const base = ensureDraft();
    setDraft(base.map((item) =>
      item.sk === sk ? { ...item, [field]: Math.max(0, value) } : item,
    ));
  }

  function handleConfirm() {
    if (draft) onSave(draft);
    setShowConfirm(false);
    setDraft(null);
  }

  const currentAll = draft ?? data;
  const isDirty = draft !== null;
  const currentTotal = currentAll.find((l) => l.limit_type === 'total_portfolio');
  const currentLevels = currentAll
    .filter((l) => l.limit_type === 'per_level')
    .sort((a, b) => {
      const order: Record<string, number> = { BRONZE: 1, SILVER: 2, GOLD: 3, PLATINUM: 4 };
      return (order[a.level_name ?? ''] ?? 99) - (order[b.level_name ?? ''] ?? 99);
    });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Controla la exposicion maxima del portafolio total y por nivel crediticio.
        </p>
        <button
          type="button"
          onClick={() => setShowInfo(true)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-sky-400 transition-colors hover:bg-sky-500/15"
        >
          <Info size={14} />
          Que pasa al llegar al limite
        </button>
      </div>

      {/* Total portfolio gauge */}
      {currentTotal && (() => {
        const pctAmount = usagePct(currentTotal.current_amount_mxn, currentTotal.max_amount_mxn);
        const pctCredits = usagePct(currentTotal.current_credits, currentTotal.max_credits);
        const mainPct = Math.max(pctAmount, pctCredits);
        const barColor = usageColor(mainPct, currentTotal.alert_threshold_pct, currentTotal.block_threshold_pct);

        return (
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-bold text-foreground">Portafolio Total</span>
              <span className="text-xs font-semibold" style={{ color: barColor }}>
                {mainPct.toFixed(1)}% utilizado
              </span>
            </div>

            {/* Main usage bar */}
            <UsageBar
              current={currentTotal.current_amount_mxn}
              max={currentTotal.max_amount_mxn}
              alert={currentTotal.alert_threshold_pct}
              block={currentTotal.block_threshold_pct}
            />

            {/* Legend */}
            <div className="mt-1.5 flex items-center gap-4 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" /> Alerta {currentTotal.alert_threshold_pct}%</span>
              <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" /> Bloqueo {currentTotal.block_threshold_pct}%</span>
            </div>

            {/* Stats grid */}
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <span className="text-[10px] text-muted-foreground">Monto maximo</span>
                {canEdit ? (
                  <CurrencyInput
                    value={currentTotal.max_amount_mxn}
                    onChange={(v) => updateItem(currentTotal.sk, 'max_amount_mxn', v)}
                    className="mt-0.5 block h-7 w-full rounded border border-sky-500/30 bg-muted/50 px-2 text-sm font-bold text-sky-400 focus:outline-none"
                  />
                ) : (
                  <p className="mt-0.5 text-lg font-bold text-foreground">{formatCurrency(currentTotal.max_amount_mxn)}</p>
                )}
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground">Creditos max</span>
                {canEdit ? (
                  <NumberInput
                    value={currentTotal.max_credits}
                    onChange={(v) => updateItem(currentTotal.sk, 'max_credits', v)}
                    className="mt-0.5 block h-7 w-full rounded border border-sky-500/30 bg-muted/50 px-2 text-sm font-bold text-sky-400 focus:outline-none"
                  />
                ) : (
                  <p className="mt-0.5 text-lg font-bold text-foreground">{formatNumber(currentTotal.max_credits)}</p>
                )}
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground">Monto actual</span>
                <p className="mt-0.5 text-lg font-semibold text-foreground">{formatCurrency(currentTotal.current_amount_mxn)}</p>
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground">Creditos activos</span>
                <p className="mt-0.5 text-lg font-semibold text-foreground">{currentTotal.current_credits}</p>
              </div>
            </div>

            {/* Threshold editors */}
            {canEdit && (
              <div className="mt-3 flex gap-3 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-1.5">
                  <ShieldAlert size={12} className="text-amber-500" />
                  <span className="text-[10px] text-muted-foreground">Alerta</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={currentTotal.alert_threshold_pct}
                    onChange={(e) => updateItem(currentTotal.sk, 'alert_threshold_pct', Number(e.target.value))}
                    className="h-6 w-14 rounded border border-amber-500/30 bg-muted/50 px-1.5 text-center text-xs font-semibold text-amber-500 focus:outline-none"
                  />
                  <span className="text-[10px] text-muted-foreground">%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Ban size={12} className="text-red-500" />
                  <span className="text-[10px] text-muted-foreground">Bloqueo</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={currentTotal.block_threshold_pct}
                    onChange={(e) => updateItem(currentTotal.sk, 'block_threshold_pct', Number(e.target.value))}
                    className="h-6 w-14 rounded border border-red-500/30 bg-muted/50 px-1.5 text-center text-xs font-semibold text-red-500 focus:outline-none"
                  />
                  <span className="text-[10px] text-muted-foreground">%</span>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Per-level cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {currentLevels.map((limit) => {
          const theme = LEVEL_THEMES[limit.level_name ?? ''] ?? { color: '#666', label: limit.level_name ?? '?' };
          const pctAmount = usagePct(limit.current_amount_mxn, limit.max_amount_mxn);
          const pctCredits = usagePct(limit.current_credits, limit.max_credits);
          const mainPct = Math.max(pctAmount, pctCredits);
          const barColor = usageColor(mainPct, limit.alert_threshold_pct, limit.block_threshold_pct);

          return (
            <div
              key={limit.sk}
              className="rounded-lg border bg-card p-3 border-l-[3px]"
              style={{ borderLeftColor: theme.color }}
            >
              {/* Header */}
              <div className="mb-2 flex items-center justify-between">
                <span
                  className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold uppercase"
                  style={{ backgroundColor: `${theme.color}18`, color: theme.color }}
                >
                  {theme.label}
                </span>
                <span className="text-xs font-semibold" style={{ color: barColor }}>
                  {mainPct.toFixed(0)}%
                </span>
              </div>

              {/* Usage bar */}
              <UsageBar
                current={limit.current_amount_mxn}
                max={limit.max_amount_mxn}
                alert={limit.alert_threshold_pct}
                block={limit.block_threshold_pct}
                />

              {/* Stats */}
              <div className="mt-2.5 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Monto max</span>
                  {canEdit ? (
                    <CurrencyInput
                      value={limit.max_amount_mxn}
                      onChange={(v) => updateItem(limit.sk, 'max_amount_mxn', v)}
                      className="h-6 w-28 rounded border bg-muted/50 px-1.5 text-right text-xs font-semibold focus:outline-none"
                    />
                  ) : (
                    <span className="font-semibold" style={{ color: theme.color }}>{formatCurrency(limit.max_amount_mxn)}</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Creditos max</span>
                  {canEdit ? (
                    <NumberInput
                      value={limit.max_credits}
                      onChange={(v) => updateItem(limit.sk, 'max_credits', v)}
                      className="h-6 w-28 rounded border bg-muted/50 px-1.5 text-right text-xs font-semibold focus:outline-none"
                    />
                  ) : (
                    <span className="font-semibold" style={{ color: theme.color }}>{formatNumber(limit.max_credits)}</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Actual</span>
                  <span className="font-semibold text-foreground">
                    {formatCurrency(limit.current_amount_mxn)} / {limit.current_credits} cr
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save bar */}
      {isDirty && (
        <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-lg border bg-card p-2.5 shadow-lg">
          <span className="mr-auto text-xs text-muted-foreground">Cambios sin guardar</span>
          <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
            Descartar
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Save size={14} />} onClick={() => setShowConfirm(true)}>
            Guardar
          </Button>
        </div>
      )}

      {/* Info modal */}
      <Modal
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        title="Limites de exposicion"
        size="md"
      >
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Los limites controlan cuanto credito puede tener vivo el portafolio. Existen dos umbrales de proteccion.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <ShieldAlert size={14} className="text-amber-400" />
                <span className="text-xs font-bold text-amber-400">Umbral de alerta</span>
              </div>
              <div className="space-y-1 text-[11px] text-amber-400/80">
                <p>Se activa al llegar al porcentaje configurado.</p>
                <p><strong>Efecto:</strong> Se notifica a los operadores. Se pueden seguir originando creditos pero con precaucion.</p>
                <p>Tipicamente 70-80% del limite.</p>
              </div>
            </div>
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <Ban size={14} className="text-red-400" />
                <span className="text-xs font-bold text-red-400">Umbral de bloqueo</span>
              </div>
              <div className="space-y-1 text-[11px] text-red-400/80">
                <p>Se activa al llegar al porcentaje configurado.</p>
                <p><strong>Efecto:</strong> Se detiene la originacion de nuevos creditos para ese nivel (o total). Los creditos existentes no se afectan.</p>
                <p>Tipicamente 90-95% del limite.</p>
              </div>
            </div>
          </div>

          <div className="space-y-0">
            {[
              { icon: '1', text: 'Se origina un nuevo credito → se suma al monto y conteo actual' },
              { icon: '2', text: 'Si el uso supera el umbral de alerta → notificacion al equipo' },
              { icon: '3', text: 'Si supera el umbral de bloqueo → no se generan mas ofertas para ese nivel' },
              { icon: '4', text: 'Cuando un credito se liquida → el espacio se libera automaticamente' },
            ].map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                    {step.icon}
                  </span>
                  {i < 3 && <div className="w-px flex-1 bg-border my-0.5" />}
                </div>
                <p className="pb-3 text-sm text-foreground">{step.text}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-muted/50 px-4 py-2.5">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Configuracion:</strong> Puedes modificar los montos maximos, numero de creditos, y los umbrales de alerta/bloqueo. Los limites por nivel permiten controlar la concentracion de riesgo — evitar que un solo nivel (ej. BRONZE) acapare todo el portafolio.
            </p>
          </div>
        </div>
      </Modal>

      <ConfirmSaveModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        isSaving={isSaving}
      />
    </div>
  );
}
